import { useForzarModoClaro } from '@/lib/hooks/use-forzar-modo-claro';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Trash2, Settings } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { EncabezadoDeGestion } from '@/features/cotiza/components/encabezado-de-gestion';
import { Button } from '@/components/ui/button';
import { pricerDatecService } from '../services/pricer-datec.service';
import { fmtUSD, fmtFecha } from '../lib/calc';
import { margenParaLista } from '../utils/ficha-de-observador';
import { PricerDatecStyles } from '../components/styles';
import { useToast } from '@/lib/hooks/use-toast';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { COLORES_DE_ESTADO, etiquetaDeEstado } from '../components/estado-cotizacion';
import { nombreDeClienteParaMostrar } from '../lib/nombre-cliente';
import { sePuedeEliminar } from '../utils/se-puede-eliminar';
import { DealPill } from '@/features/opportunities/components/deal-pill';
import type { PricerDatecQuoteSummary } from '../types/pricer-datec.types';

const STATUS_COLORS = COLORES_DE_ESTADO;

export function PricerDatecListPage() {
  useForzarModoClaro();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [quotes, setQuotes] = useState<PricerDatecQuoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasDeal, setHasDeal] = useState<'all' | 'true' | 'false'>('all');

  useEffect(() => {
    setLoading(true);
    pricerDatecService
      .listQuotes({ hasDeal: hasDeal === 'all' ? undefined : hasDeal })
      .then(setQuotes)
      .finally(() => setLoading(false));
  }, [hasDeal]);

  // FP T13b: la papelera la decide el servidor por fila (`permisos.puedeBorrar`: el creador, o quien edita las de
  // toda la empresa y la ve). Quien la tiene compartida no la ve. Sin el bloque, no se ofrece.
  const puedeBorrar = (q: PricerDatecQuoteSummary) =>
    sePuedeEliminar(q) && !!q.permisos?.puedeBorrar;

  const eliminar = (q: PricerDatecQuoteSummary) =>
    confirm({
      title: `¿Eliminar el borrador ${q.code}?`,
      description: 'No se puede deshacer.',
      confirmText: 'Eliminar',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await pricerDatecService.deleteQuote(q.id);
          setQuotes((prev) => prev.filter((x) => x.id !== q.id));
          toast({ title: 'Borrador eliminado', description: q.code });
        } catch (e) {
          toast({
            title: 'No se pudo eliminar',
            description: mensajeDeError(e, 'Probá de nuevo en unos minutos.'),
            variant: 'destructive',
          });
        }
      },
    });

  // El observador no ve márgenes: si ninguna fila trae uno, la columna no existe (F6 ronda T14).
  const verMargen = quotes.some((q) => typeof q.margenGlobal === 'number');

  return (
    <AppLayout>
      <div className="pd-root mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <PricerDatecStyles />

        {/*
          El encabezado de Gestión (artefacto, 19-oct): el paso «Cotizaciones».
          Sólo cambia el encabezado; la lista y sus reglas son las de siempre.
        */}
        <div style={{ marginBottom: 24 }}>
          <EncabezadoDeGestion
            activa="cotizaciones"
            titulo="Cotizaciones"
            subtitulo={`${quotes.length} ${quotes.length === 1 ? 'cotización' : 'cotizaciones'}`}
            acciones={
              <>
                {hasPermission('configure_pricer') && (
                  <Button variant="outline" onClick={() => navigate('/pricer-datec/configuracion')}>
                    <Settings size={14} aria-hidden="true" /> Configuración del Pricer
                  </Button>
                )}
                {/* FP-4: cotizar en el Pricer pide «Ver costos y márgenes» (quien no la tiene sólo mira). */}
                {hasPermission('view_costs') && (
                  <Button
                    variant="degradado"
                    size="lg"
                    className="px-5"
                    onClick={() => navigate('/pricer-datec/new')}
                  >
                    <Plus size={14} aria-hidden="true" /> Nueva cotización
                  </Button>
                )}
              </>
            }
          />
        </div>

        {/* Filtros */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <select
            value={hasDeal}
            onChange={(e) => setHasDeal(e.target.value as 'all' | 'true' | 'false')}
            style={{
              padding: '7px 12px',
              border: '1px solid hsl(var(--muted-foreground) / 0.4)',
              borderRadius: 6,
              fontSize: 13,
              color: 'hsl(var(--foreground) / 0.8)',
              background: 'hsl(var(--card))',
            }}
          >
            <option value="all">Todas las cotizaciones</option>
            <option value="true">Con oportunidad</option>
            <option value="false">Sin oportunidad</option>
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
            Cargando…
          </div>
        ) : quotes.length === 0 ? (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              color: 'hsl(var(--muted-foreground))',
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 10,
            }}
          >
            <FileText
              size={32}
              color="hsl(var(--muted-foreground))"
              style={{ margin: '0 auto 12px' }}
            />
            {/*
              Desde F7 cada quien ve sólo lo suyo, así que «no hay cotizaciones»
              sería mentira: hay, pero no son de esta persona. Tiene que decir
              qué pasó y qué hacer.
            */}
            <div
              style={{ fontWeight: 600, color: 'hsl(var(--foreground) / 0.8)', marginBottom: 4 }}
            >
              Todavía no creaste ninguna, y nadie te compartió una
            </div>
            <div style={{ fontSize: 13 }}>
              Cada cotización la ve quien la creó. Si necesitás ver la de un compañero, pedile que
              te la comparta desde el botón «Acceso» de la ficha.
            </div>
          </div>
        ) : (
          <div
            style={{
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 10,
              // Con muchas columnas la tabla se desplaza de costado: antes las últimas (Creador y
              // el botón de borrar) quedaban recortadas y no se podían alcanzar.
              overflowX: 'auto',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr
                  style={{
                    background: 'hsl(var(--muted) / 0.5)',
                    borderBottom: '1px solid hsl(var(--border))',
                  }}
                >
                  <th style={th}>Código</th>
                  <th style={th}>Título</th>
                  <th style={th}>Cliente</th>
                  <th style={th}>Oportunidad</th>
                  {/* Tipo y Creador sólo en pantallas muy anchas: con las doce columnas, a 1440 px el Estado
                      quedaba fuera de la vista. */}
                  <th style={th} className={SOLO_MUY_ANCHA}>
                    Tipo
                  </th>
                  <th style={th}>Fecha</th>
                  <th style={{ ...th, textAlign: 'right' }}>Ítems</th>
                  <th style={{ ...th, textAlign: 'right' }}>Total</th>
                  {verMargen && <th style={{ ...th, textAlign: 'right' }}>Margen Bruto</th>}
                  <th style={th}>Estado</th>
                  <th style={th} className={SOLO_MUY_ANCHA}>
                    Creador
                  </th>
                  <th style={th} aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => {
                  const sc = STATUS_COLORS[q.status] ?? STATUS_COLORS.DRAFT;
                  return (
                    <tr
                      key={q.id}
                      onClick={() => navigate(`/pricer-datec/${q.id}`)}
                      className="pd-table-row"
                      style={{ borderBottom: '1px solid hsl(var(--border))', cursor: 'pointer' }}
                    >
                      <td style={tdCorta}>
                        <span className="pd-mono" style={{ fontWeight: 600 }}>
                          {q.code}
                        </span>
                      </td>
                      <td
                        title={q.name || undefined}
                        style={{
                          ...td,
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {q.name || (
                          <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: 12 }}>
                            —
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, fontWeight: 500, minWidth: 140 }}>
                        {nombreDeClienteParaMostrar(q.clientName) || (
                          <span style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>
                            Sin cliente
                          </span>
                        )}
                      </td>
                      <td style={td}>
                        {q.hubspotDeal ? (
                          <DealPill deal={q.hubspotDeal} variant="compact" />
                        ) : (
                          <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: 12 }}>
                            —
                          </span>
                        )}
                      </td>
                      <td style={tdCorta} className={SOLO_MUY_ANCHA}>
                        {q.tipoQuote}
                      </td>
                      <td style={tdCorta}>{fmtFecha(q.fecha)}</td>
                      <td style={{ ...tdCorta, textAlign: 'right' }}>{q.itemsCount}</td>
                      <td
                        style={{
                          ...tdCorta,
                          textAlign: 'right',
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontWeight: 600,
                        }}
                      >
                        ${fmtUSD(q.totalConBoleta)}
                      </td>
                      {verMargen && (
                        <td
                          style={{
                            ...tdCorta,
                            textAlign: 'right',
                            fontFamily: '"IBM Plex Mono", monospace',
                          }}
                        >
                          {margenParaLista(q.margenGlobal)}
                        </td>
                      )}
                      <td style={tdCorta}>
                        <span
                          className="pd-pill"
                          style={{ background: sc.bg, color: sc.color, fontSize: 12 }}
                        >
                          {
                            etiquetaDeEstado(
                              q.status,
                              q.reaperturaPendiente === true,
                              q.retirada === true,
                              q.reabierta === true
                            ).texto
                          }
                        </span>
                      </td>
                      <td
                        className={SOLO_MUY_ANCHA}
                        style={{ ...td, color: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      >
                        {q.createdBy?.firstName} {q.createdBy?.lastName}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {puedeBorrar(q) && (
                          <button
                            type="button"
                            aria-label="Eliminar borrador"
                            title="Eliminar borrador"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              eliminar(q);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'hsl(var(--bloqueo))',
                              cursor: 'pointer',
                              padding: 4,
                            }}
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmDialog />
    </AppLayout>
  );
}

/** Columnas que sólo se muestran desde 1536 px. */
const SOLO_MUY_ANCHA = 'hidden 2xl:table-cell';

const th: React.CSSProperties = {
  padding: '12px 10px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'hsl(var(--foreground) / 0.8)',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '14px 10px',
  fontSize: 13,
  color: 'hsl(var(--foreground))',
};

/** Las celdas cortas (código, fecha, montos, estado) no se parten en renglones. */
const tdCorta: React.CSSProperties = { ...td, whiteSpace: 'nowrap' };
